import { Typography, CardMedia, Tooltip, Box, Divider, useTheme } from "@mui/material";
import Stack from "@mui/material/Stack";

import React, { useState } from "react";

import MoreHorizIcon from "@mui/icons-material/MoreHoriz";

import { ReactComponent as CollectionsLogo } from "assets/icons/bookmark-blue-icon.svg";
import { ReactComponent as CollectionsLogoHover } from "assets/icons/bookmark-hover-icon.svg";
import { HiOutlineLockClosed } from "react-icons/hi2";
import { ImInfo } from "react-icons/im";

import { useDispatch } from "react-redux";
import {
  setBookmarkedCollectionIds,
  setCollectionId,
  setCollectionSourceId,
  setEntityName,
  setIsCitationAvailable,
  setIsSidebarOpen,
  setIsSourceSidebarOpen,
  setIsSummaryAvailable,
  setLevel,
  setOthersNeeded,
  setReadingLevelAvailable,
  setSelectedBookmarkOption,
  setSourceUrl,
  setSummaryInfoData,
} from "context/features/dataSlice";
import defaultBackground from "assets/images/image-placeholder.png";
import { ReactComponent as EbscoLogo } from "assets/icons/EBSCO_Information_Services_20xx_logo.svg";
import proQuestLogo from "assets/icons/proquest-logo.svg";
import NewsguardScore from "components/base/NewsguardScore";
import AllSidesRating from "components/base/all-sides/AllSidesRating";
import { logEvent } from "services/amplitude.service.js";
import CharityNavigatorScore from "components/base/CharityNavigatorScore";
import SearchResultLink from "features/SourceSearch/components/SearchResultLink";
import FileTypeIcon from "components/base/FileTypeIcon";
import { devViewPdf } from "config";
import { showNewsguard } from "utils/show-ratings";
import OpenAccessChip from "../open-access-chip";
import SmileyIcon from "features/News/components/smiley-icon";
import AccessibleIconButton from "components/base/AccessibleIconButton";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

export const CategoryCard = ({ data = {}, cardRef, category, query }) => {
  const theme = useTheme();
  const [isHovered, setIsHovered] = useState(false);
  const [ebscoImageError, setEbscoImageError] = useState(false);

  const dispatch = useDispatch();
  const isEbsco = String(category).toLowerCase() === "ebsco";
  const description = ["ebsco", "proquest"].includes(String(category).toLowerCase()) && data.snippet?.length > 200
    ? `${data.snippet.slice(0, 197).trimEnd()}...`
    : data.snippet;
  const isDatabaseProvider = ["ebsco", "proquest"].includes(
    String(category).toLowerCase()
  );
  const hasPublisherInfo = !isDatabaseProvider && data.hostName != null;

  // const imageUrl = data?.openGraphImage?.contentUrl
  //   ? data?.openGraphImage?.contentUrl
  //   : defaultBackground;

  const openSourceSidebar = () => {
    if (isDatabaseProvider) return;
    logEvent("click_card_header", {
      host: data.hostName,
      entity: data.entityName,
    });
    dispatch(setOthersNeeded(true));
    dispatch(setIsSourceSidebarOpen(true));
    dispatch(setSourceUrl(data.hostName));
    dispatch(setEntityName(data.entityName));
  };

  const newUrl = (() => {
    let updatedUrl = data.url;

    if (updatedUrl?.includes("foreignpolicy.com")) {
      updatedUrl += "?auth=sooth";
    }

    return updatedUrl;
  })();

  const openSidebar = () => {
    logEvent("open_bookmark_popup", {
      url: newUrl,
      query: query,
      entity: data.entityName,
      category: category,
      type: "individual-category",
      mediabiasrating: data.mediaBiasRating ? data.mediaBiasRating : "NA",
      score: showNewsguard(data.score) ? data.score : "NA",
    });
    const collectionIds = (data.collectionMemberships || [])
      .map((membership) => membership.collectionID_lng)
      .filter(Boolean);

    dispatch(setSelectedBookmarkOption("notes"));
    dispatch(setLevel(""));
    setIsHovered(false);
    dispatch(setCollectionSourceId(data.collectionSourceId));
    dispatch(
      setSummaryInfoData({
        publisher: data.entityName,
        title: data.name,
        published_date: data.datePublishedDisplayText,
        url: data.url,
        utc_date_published: data.datePublished,
        isDatabaseSearch: isDatabaseProvider,
        dbId: isEbsco ? data.dbId ?? data.DbId : undefined,
        an: isEbsco ? data.an ?? data.An : undefined,
      }),
    );
    dispatch(setCollectionId(data.collectionId ?? collectionIds[0]));
    dispatch(
      setBookmarkedCollectionIds(
        collectionIds.length > 0
          ? collectionIds
          : data.collectionId
          ? [data.collectionId]
          : []
      )
    );
    dispatch(setIsCitationAvailable(data.isCitationAvailable));
    dispatch(setIsSummaryAvailable(data.isSummaryAvailable));
    dispatch(setReadingLevelAvailable(data.isReadingLevelAvailable));
    dispatch(setIsSidebarOpen(true));
  };

  const BookmarkOptions = () => {
    return (
      <Stack
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={() => {
          openSidebar();
          setIsHovered(false);
        }}
        sx={{
          cursor: "pointer",
          pl: 1,
          pr: 1,
          borderRadius: 1,
          "&:hover": {
            backgroundColor: "secondary.main",
          },
        }}
        direction="row"
        justifyContent="flex-end"
        alignItems="center"
        gap={1}
      >
        {data.collectionSourceId ? (
          <CollectionsLogo
            onClick={openSidebar}
            alt="Collections"
            style={{
              width: 18,
              height: 18,
              fill: "blue",
              color: "blue",
            }}
          />
        ) : (
          <Box
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            sx={{
              width: 17,
              height: 17,
            }}
          >
            {isHovered && (
              <CollectionsLogoHover
                onClick={openSidebar}
                alt="Collections"
                style={{
                  width: 17,
                  height: 17,
                  fill: "blue",
                  color: "blue",
                }}
              />
            )}
          </Box>
        )}
        <AccessibleIconButton
          sx={{
            p: 0,
            "&:hover": {
              backgroundColor: "transparent",
            },
          }}
          title="Open Bookmark"
          onClick={openSidebar}
        >
          <MoreHorizIcon />
        </AccessibleIconButton>
      </Stack>
    );
  };

  const DatabaseDetails = () => {
    const authors = Array.isArray(data.authors)
      ? data.authors.filter((author) => typeof author === "string" && author.trim())
      : [];
    const database = data.dbLabel || data.dbId;
    const source = data.sourceTitle || data.entityName;
    const edition = [
      data.volume && `Vol. ${data.volume}`,
      data.issue && `Issue ${data.issue}`,
    ].filter(Boolean).join(", ");
    // The API must supply the full article count; the snippet is only an excerpt.
    const wordCount = data.wordCount;
    const hasWordCount = typeof wordCount === "number" &&
      Number.isInteger(wordCount) && wordCount >= 0;
    const badgeSx = {
      px: 0.75, py: 0.25, borderRadius: 0.5,
      bgcolor: "action.hover", typography: "body2", fontWeight: 600,
      display: "inline-flex", alignItems: "center", gap: 0.5,
    };

    return (
      <Stack gap={0.5} sx={{ minWidth: 0, overflowWrap: "anywhere" }}>
        {authors.length > 0 && (
          <Tooltip title={authors.join("; ")} enterDelay={750} placement="bottom-start">
            <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>
              <strong>{authors.length === 1 ? "Author:" : "Authors:"}</strong>{" "}
              {authors.join("; ")}
            </Typography>
          </Tooltip>
        )}
        {(source || edition || data.isPeerReviewed === true) && (
          <Stack direction="row" flexWrap="wrap" gap={1} alignItems="center">
            {(source || edition) && (
              <Tooltip title={[source, edition].filter(Boolean).join(" ")} enterDelay={750} placement="bottom-start">
                <Typography variant="body2" noWrap sx={{ minWidth: 0, maxWidth: "100%" }}>
                  <strong>From:</strong>{" "}
                  {source}
                  {edition && ` (${edition})`}
                </Typography>
              </Tooltip>
            )}
            {data.isPeerReviewed === true && (
              <Typography variant="body2" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                <CheckCircleIcon sx={{ fontSize: 16 }} />Peer-Reviewed
              </Typography>
            )}
          </Stack>
        )}
        <Stack direction="row" flexWrap="wrap" gap={0.75} alignItems="center" sx={{ mt: 1 }}>
          {hasWordCount && <Box component="span" sx={badgeSx}>{wordCount.toLocaleString("en-US")} words</Box>}
          {data.docType && <Box component="span" sx={badgeSx}>{data.docType}</Box>}
          {data.hasPdf === true && (
            <Box component="span" sx={badgeSx} aria-label="PDF available">
              <PictureAsPdfOutlinedIcon sx={{ fontSize: 16 }} />PDF
            </Box>
          )}
          {data.hasOnlineFullText === true && (
            <Box component="span" sx={badgeSx} aria-label="Online full text available">
              <ArticleOutlinedIcon sx={{ fontSize: 16 }} />Online full text
            </Box>
          )}
        </Stack>
        {database && <Typography variant="body2" color="text.secondary">Database: {database}</Typography>}
      </Stack>
    );
  };

  const ImageSection = () => {
    const imageUrl = data?.openGraphImage?.contentUrl;

    const [errored, setErrored] = useState(false);

    function onError({ currentTarget }) {
      if (!errored) {
        setErrored(true);
        currentTarget.src = defaultBackground;
        currentTarget.alt = "Image not found";
      }
    }
    return (
      <Box>
        <CardMedia
          component="img"
          sx={{
            aspectRatio: 1,
            borderRadius: "10px",
          }}
          image={imageUrl == null ? defaultBackground : imageUrl}
          alt={imageUrl == null ? "Image not found" : data?.name}
          onError={onError}
        />
      </Box>
    );
  };

  if (isDatabaseProvider) {
    const hasEbscoImage = isEbsco && Boolean(data.openGraphImage?.contentUrl) && !ebscoImageError;

    return (
      <Box
        ref={cardRef}
        component="article"
        sx={{ mt: 2, mb: 2, pb: 2.5, px: 1, borderBottom: 1, borderColor: "divider" }}
      >
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {data.entityName && (
              <Typography
                variant="body1"
                sx={{ fontWeight: "bold", color: "grey.600", mb: 1 }}
              >
                {data.entityName}
              </Typography>
            )}
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 0.75 }}>
              <SearchResultLink
                url={data.url}
                trackingCat="click_resultset_title"
                trackingOptions={{ url: data.url, query, entity: data.entityName, category, title: data.name, date_published: data.datePublished }}
                variant="body4"
                sx={{ flex: 1, minWidth: 0, color: "primary.main", lineHeight: 1.4, overflowWrap: "anywhere", "&:hover": { textDecoration: "underline" } }}
              >
                <Box component="span" sx={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                  {data.name}
                </Box>
              </SearchResultLink>
            </Box>
            <DatabaseDetails />
            {(description || data.datePublishedDisplayText) && (
              <Typography variant="body1" sx={{ mt: 1.5, mb: 0, lineHeight: 1.5, overflowWrap: "anywhere" }}>
                {data.datePublishedDisplayText && `${data.datePublishedDisplayText}${description ? " - " : ""}`}
                {description}
              </Typography>
            )}
          </Box>
          <Box sx={{ width: { xs: 110, sm: 185 }, minHeight: { xs: 110, sm: 185 }, flexShrink: 0, display: "flex", flexDirection: "column", justifyContent: "space-between", alignItems: "flex-end" }}>
            {hasEbscoImage ? (
              <CardMedia
                component="img"
                src={data.openGraphImage.contentUrl}
                alt={data.name || "EBSCO result"}
                onError={() => setEbscoImageError(true)}
                sx={{
                  width: { xs: 72, sm: 110 },
                  height: { xs: 72, sm: 110 },
                  objectFit: "contain",
                  borderRadius: 1,
                }}
              />
            ) : (
              <Box
                sx={{
                  width: "100%",
                  height: { xs: 110, sm: 185 },
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: 1,
                  overflow: "hidden",
                }}
              >
                {isEbsco ? (
                  <EbscoLogo aria-label="EBSCO Information Services" style={{ width: 110, height: 110 }} />
                ) : (
                  <Box title="ProQuest" role="img" aria-label="ProQuest" className="pq-header-logo"
                    sx={{ width: 110, height: 110, filter: theme.palette.mode === "dark" ? "none" : "brightness(0)", backgroundImage: `url('${proQuestLogo}')`, backgroundSize: "contain", backgroundRepeat: "no-repeat", backgroundPosition: "center" }} />
                )}
              </Box>
            )}
            <BookmarkOptions />
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      ref={cardRef}
      // container
      spacing={2}
      sx={{
        display: "grid",
        gridTemplateAreas: `
      'sourceSidebar sourceSidebar sourceSidebar'
      'content content content'
      'divider divider divider'`,
        gridTemplateColumns: "3fr 0.2fr 1fr",
        gridTemplateRows: "auto 1fr auto",
        padding: 0,
        mb: 1,
      }}
    >
      {/* Sidebar Section */}
      {!isDatabaseProvider && <Box
        // item
        sx={{
          gridArea: "sourceSidebar",
          mb: 2,
          display: "flex",
          alignItems: "center",
          cursor: hasPublisherInfo ? "pointer" : "default",
          ...(hasPublisherInfo && {
            "&:hover": { backgroundColor: "secondary.main" },
          }),
          borderRadius: 1,
          py: 0.4,
          px: 1,
        }}
        onClick={isDatabaseProvider ? undefined : openSourceSidebar}
      >
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}
        >
          <Typography
            variant="body1"
            sx={{ fontWeight: "bold", color: "grey.600" }}
          >
            {data.entityName}
          </Typography>
          {!isDatabaseProvider && !hasPublisherInfo && (
            <Tooltip title="No source information available">
              <Box
                component="span"
                sx={{ display: "inline-flex", color: "grey.500" }}
              >
                <ImInfo size={15} />
              </Box>
            </Tooltip>
          )}
        </Box>

        <Box
          sx={{
            ml: "auto",
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 2,
            width: { md: "12vw", lg: "10vw" },
            minWidth: "160px",
          }}
        >
          {data.hasOpenAccess && <OpenAccessChip />}
          {data.mediaBiasRating && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                width: { md: "8vw", lg: "7vw" },
                minWidth: "80px",
                ml: 1,
              }}
            >
              <AllSidesRating activeCategory={data.mediaBiasRating} />
            </Box>
          )}
          {showNewsguard(data.score) && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                width: { md: "5vw", lg: "3vw" },
                minWidth: "50px",
              }}
            >
              <NewsguardScore score={Number(data.score)} />
            </Box>
          )}

          {data.charityNavigatorScore &&
            category !== "Academia & Scholarly Resources" &&
            !data.score &&
            !data.mediaBiasRating && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  width: { md: "5vw", lg: "3vw" },
                  minWidth: "50px",
                }}
              >
                <CharityNavigatorScore
                  score={data.charityNavigatorScore}
                  size="small"
                />
              </Box>
            )}
        </Box>
      </Box>}

      {/* Content Section: Title, Description, and Image */}
      <Box
        // item
        sx={{
          gridArea: "content",
          display: "flex",
          gap: 2,
          alignItems: "flex-start",
          pl: 1,
        }}
      >
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1 }}>
          {/* Title */}
          <Box
            sx={{
              display: "flex",
              alignItems: "start",
              justifyContent: "space-between",
            }}
          >
            <SearchResultLink
              url={data.url}
              trackingCat="click_resultset_title"
              trackingOptions={{
                url: data.url,
                query: query,
                entity: data.entityName,
                category: category,
                mediabiasrating: data.mediaBiasRating
                  ? data.mediaBiasRating
                  : "NA",
                score: showNewsguard(data.score) ? data.score : "NA",
                title: data.name,
                date_published: data.datePublished,
              }}
              variant="body4"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                lineHeight: "1.5em",
                maxWidth: "90%",
                flexShrink: 0,
                cursor: "pointer",
                "&:hover": {
                  color: "rgb(18,89,151)",
                },
              }}
            >
              {data.name}
            </SearchResultLink>
            {devViewPdf && (
              <FileTypeIcon url={data.url} sx={{ flexShrink: 0, mt: "2px" }} />
            )}
            {data.hasPayWall && data.isPartner ? (
              <SmileyIcon />
            ) : (
              data.hasPayWall && (
                <Tooltip
                  placement="bottom-start"
                  title="This source may limit your access without a subscription"
                >
                  <Box sx={{ cursor: "pointer", mt: "0.2em" }}>
                    <HiOutlineLockClosed />
                  </Box>
                </Tooltip>
              )
            )}
          </Box>

          {isDatabaseProvider && <DatabaseDetails />}
          {/* Description and Bookmark Options in a Row */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            mt={1}
          >
            <Typography
              variant="body2"
              color="textSecondary"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: { xs: 3, sm: 5 },
                WebkitBoxOrient: "vertical",
                maxHeight: { xs: "6vh", sm: "10vh" },
                lineHeight: { xs: "2vh", sm: "2vh" },
                width: "90%",
              }}
            >
              {!isDatabaseProvider && data.datePublishedDisplayText &&
                `${data.datePublishedDisplayText} - `}
              {description}
            </Typography>

            {/* Bookmark Options */}
          </Stack>
        </Box>

        {/* Image Section */}
        <Box
          // item
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "end",
            width: isDatabaseProvider ? "auto" : "10vw",
            pr: 1,
          }}
        >
          {!isDatabaseProvider && <ImageSection />}
          <Box sx={{ mt: 1 }}>
            <BookmarkOptions />
          </Box>
        </Box>
      </Box>

      {/* Divider Section */}
      <Box
        // item
        sx={{
          gridArea: "divider",
          mt: 1,
          paddingLeft: 0,
        }}
      >
        <Divider />
      </Box>
    </Box>
  );
};
